import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../assets/css/ForgotPassword.css";
import axiosInstance from "../components/api/axiosInstance";

function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Enter email, 2: Enter OTP and new password
  const [email, setEmail] = useState("");
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const otpInputRefs = useRef([]);
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: ""
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  // Get frontend URL from environment or use current origin
  const getFrontendUrl = () => {
    return process.env.REACT_APP_FRONTEND_URL || window.location.origin;
  };

  const handleNext = async (e) => {
    e.preventDefault();

    if (!email) {
      setError("Company email is required");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const frontendUrl = getFrontendUrl();
      const response = await axiosInstance.post('/forgot-password', {
        company_email: email,
        frontend_url: frontendUrl
      });
      
      if (response.data && response.data.status === "success") {
        setStep(2); // Move to OTP verification step
      } else {
        const errorMsg = response.data?.message || "Failed to send OTP. Please try again.";
        setError(errorMsg);
        console.error("Forgot password failed:", errorMsg);
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || (error.request ? "Network error." : "Forgot password failed.");
      console.error("Forgot password error:", msg);
      
      if (error.response && error.response.data) {
        const errorMsg = error.response.data.message || 
                        error.response.data.error || 
                        `Server error: ${error.response.status}`;
        setError(errorMsg);
      } else if (error.request) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("Failed to send OTP. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user starts typing
    if (error) setError("");
  };

  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newOtpDigits = [...otpDigits];
    newOtpDigits[index] = value;
    setOtpDigits(newOtpDigits);

    // Auto-focus to next input if value is entered
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }

    // Clear errors when user starts typing
    if (error) setError("");
  };

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
    // Handle paste
    if (e.key === "v" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, "").slice(0, 6).split("");
        const newOtpDigits = [...otpDigits];
        digits.forEach((digit, i) => {
          if (i < 6) {
            newOtpDigits[i] = digit;
          }
        });
        setOtpDigits(newOtpDigits);
        // Focus on the last filled input or the last input
        const lastFilledIndex = Math.min(digits.length - 1, 5);
        otpInputRefs.current[lastFilledIndex]?.focus();
      });
    }
  };

  // Focus first OTP input when step 2 is shown
  useEffect(() => {
    if (step === 2 && otpInputRefs.current[0]) {
      setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 100);
    }
  }, [step]);

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Combine OTP digits into a single string
    const otp = otpDigits.join("");

    // Validation
    if (!otp || otp.length !== 6 || !formData.newPassword || !formData.confirmPassword) {
      setError("All fields are required. OTP must be 6 digits.");
      return;
    }

    if (formData.newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post('/reset-password', {
        otp: otp,
        new_password: formData.newPassword
      });

      if (response.data && response.data.status === "success") {
        const successMessage = response.data.message || "Password has been reset successfully. Redirecting to login...";
        setSuccess(successMessage);
        setOtpDigits(["", "", "", "", "", ""]);
        setFormData({
          newPassword: "",
          confirmPassword: ""
        });
        
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        const errorMsg = response.data?.message || "Failed to reset password. Please try again.";
        setError(errorMsg);
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || (error.request ? "Network error." : "Reset password failed.");
      console.error("Reset password error:", msg);
      
      if (error.response && error.response.data) {
        const errorMsg = error.response.data.message || 
                        error.response.data.error || 
                        `Server error: ${error.response.status}`;
        setError(errorMsg);
      } else if (error.request) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("Failed to reset password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setEmail("");
    setOtpDigits(["", "", "", "", "", ""]);
    setFormData({
      newPassword: "",
      confirmPassword: ""
    });
    setError("");
    setSuccess("");
  };

  // Step 1: Enter email
  if (step === 1) {
    return (
      <div className="forgotpassword-container">
        <form className="forgotpassword-form" onSubmit={handleNext}>
          <h2>Forgot Password</h2>
          <p className="forgotpassword-description">
            Enter your company email address and we'll send you an OTP to reset your password.
          </p>

          {error && <p className="error">{error}</p>}

          <div className="input-group">
            <label>Company Email</label>
            <input
              type="email"
              placeholder=""
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? "Sending..." : "Next"}
          </button>

          <div className="back-to-login">
            <button 
              type="button" 
              className="back-link" 
              onClick={() => navigate("/")}
              disabled={loading}
            >
              Back to Login
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Step 2: Enter OTP and new password
  return (
    <div className="forgotpassword-container">
      <form className="forgotpassword-form" onSubmit={handleUpdatePassword}>
        <h2>Reset Password</h2>

        {error && <p className="error">{error}</p>}
        {success && <p className="success">{success}</p>}

        <div className="input-group">
          <label>Enter OTP <span className="required">*</span></label>
          <div className="otp-input-container">
            {otpDigits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (otpInputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                className="otp-input"
                disabled={loading}
                required
              />
            ))}
          </div>
        </div>

        <div className="input-group">
          <label>New Password <span className="required">*</span></label>
          <input
            type="password"
            name="newPassword"
            placeholder=""
            value={formData.newPassword}
            onChange={handleInputChange}
            required
            disabled={loading}
          />
          <span className="hint-text">Must be at least 6 characters</span>
        </div>

        <div className="input-group">
          <label>Confirm New Password <span className="required">*</span></label>
          <input
            type="password"
            name="confirmPassword"
            placeholder=""
            value={formData.confirmPassword}
            onChange={handleInputChange}
            required
            disabled={loading}
          />
        </div>

        <button type="submit" className="submit-button" disabled={loading}>
          {loading ? "Updating..." : "Update Password"}
        </button>
      </form>
    </div>
  );
}

export default ForgotPassword;
